import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { BadgeCheck, Link2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

const DataRow = ({ label, value, testid }) => (
  <div className="border-b border-border/60 py-3 last:border-0" data-testid={testid}>
    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
    <p className="mt-1 text-sm font-medium">{value || "—"}</p>
  </div>
);

export default function EmployeeProfile() {
  const { employeeId } = useParams();
  const { user } = useAuth();
  const [employee, setEmployee] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const canLink = ["admin", "director", "hr"].includes(user?.role);

  const load = useCallback(async () => {
    try {
      const response = await api.get(`/hrms/employees/${employeeId}`);
      setEmployee(response.data);
      setError("");
      if (canLink && !response.data.user_id) {
        const accountsResponse = await api.get("/hrms/employees/accounts");
        setAccounts(accountsResponse.data);
      }
    } catch (requestError) {
      setError(requestError.response?.data?.detail || "Employee profile could not be loaded.");
    }
  }, [canLink, employeeId]);

  useEffect(() => { load(); }, [load]);

  const linkAccount = async () => {
    if (!selectedAccount) {
      toast.error("Select an existing WIPPLOPS account first.");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/hrms/employees/${employeeId}/link-user`, { user_id: selectedAccount });
      toast.success("Existing WIPPLOPS account linked to this employee record.");
      setSelectedAccount("");
      await load();
    } catch (requestError) {
      toast.error(requestError.response?.data?.detail || "Unable to link the account.");
    } finally {
      setBusy(false);
    }
  };

  if (error) {
    return (
      <p className="text-sm text-destructive" data-testid="hrms-profile-error">
        {error}
      </p>
    );
  }
  if (!employee) {
    return (
      <p className="text-sm text-muted-foreground" data-testid="hrms-profile-loading">
        Loading…
      </p>
    );
  }

  return (
    <div className="space-y-5" data-testid="hrms-employee-profile-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <Link
            className="text-sm font-semibold text-primary hover:underline"
            to="/hrms/employees"
            data-testid="hrms-profile-back-link"
          >
            Employee Management
          </Link>
          <h1 className="mt-2 text-4xl font-bold">{employee.full_name}</h1>
          <p
            className="mt-1 font-mono text-sm text-muted-foreground"
            data-testid="hrms-profile-id"
          >
            {employee.employee_id}
          </p>
        </div>
        <span className="tm-chip tm-done" data-testid="hrms-profile-status">
          {employee.employment_status}
        </span>
      </div>
      <div className="grid gap-4 lg:grid-cols-3">
        <section className="card-soft p-5">
          <BadgeCheck className="h-5 w-5 text-primary" />
          <h2 className="mt-4 text-xl font-bold">Employment</h2>
          <div className="mt-3">
            <DataRow
              label="Department"
              value={employee.department}
              testid="hrms-profile-department"
            />
            <DataRow
              label="Designation"
              value={employee.designation}
              testid="hrms-profile-designation"
            />
            <DataRow
              label="Location"
              value={employee.location}
              testid="hrms-profile-location"
            />
            <DataRow
              label="Legal entity"
              value={employee.legal_entity}
              testid="hrms-profile-entity"
            />
            <DataRow
              label="Joined"
              value={employee.joining_date}
              testid="hrms-profile-joined"
            />
          </div>
        </section>
        <section className="card-soft p-5">
          <ShieldCheck className="h-5 w-5 text-primary" />
          <h2 className="mt-4 text-xl font-bold">Contact</h2>
          <div className="mt-3">
            <DataRow
              label="Official email"
              value={employee.email}
              testid="hrms-profile-email"
            />
            <DataRow
              label="Mobile"
              value={employee.mobile}
              testid="hrms-profile-mobile"
            />
            <DataRow
              label="Reporting manager"
              value={employee.reporting_manager?.name}
              testid="hrms-profile-manager"
            />
            <DataRow
              label="Confirmation date"
              value={employee.confirmation_date}
              testid="hrms-profile-confirmation"
            />
          </div>
        </section>
        <section className="card-soft p-5" data-testid="hrms-profile-account">
          <Link2 className="h-5 w-5 text-primary" />
          <h2 className="mt-4 text-xl font-bold">WIPPLOPS account</h2>
          {employee.user_id ? (
            <p
              className="mt-3 text-sm text-muted-foreground"
              data-testid="hrms-profile-account-linked"
            >
              Linked account: {employee.linked_user?.name || employee.user_id}
            </p>
          ) : (
            <>
              <p
                className="mt-3 text-sm text-muted-foreground"
                data-testid="hrms-profile-account-pending"
              >
                No sign-in account is linked yet.
              </p>
              {canLink && (
                <div className="mt-4 space-y-3">
                  <select
                    className={[
                      "h-10 w-full rounded-md border border-input",
                      "bg-background px-3 text-sm",
                    ].join(" ")}
                    value={selectedAccount}
                    onChange={(event) => setSelectedAccount(event.target.value)}
                    data-testid="hrms-profile-account-select"
                  >
                    <option value="">Select existing account</option>
                    {accounts.map((account) => (
                      <option key={account.user_id} value={account.user_id}>
                        {account.name} · {account.email}
                      </option>
                    ))}
                  </select>
                  <Button
                    className="w-full"
                    disabled={busy}
                    onClick={linkAccount}
                    data-testid="hrms-profile-link-account-button"
                  >
                    Link existing account
                  </Button>
                </div>
              )}
            </>
          )}
        </section>
      </div>
      <section className="card-soft p-5" data-testid="hrms-profile-documents">
        <h2 className="text-xl font-bold">Employee documents</h2>
        <div className="mt-3 space-y-2">
          {(employee.documents || []).map((document) => (
            <div
              key={document.document_id}
              className="flex items-center justify-between border-b border-border/60 py-2 text-sm"
            >
              <span>{document.name}</span>
              <span className="font-mono text-xs text-muted-foreground">
                {document.document_type}
              </span>
            </div>
          ))}
          {!employee.documents?.length && (
            <p className="text-sm text-muted-foreground" data-testid="hrms-profile-documents-empty">
              No employee documents have been attached.
            </p>
          )}
        </div>
      </section>
    </div>
  );
}